import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import {
  createFeedback,
  requestFeedbackUploadIntents,
  uploadFileToSignedUrl,
  type CreateFeedbackAttachmentPayload,
} from "@/services/feedback-service";

import {
  feedbackFormSchema as feedbackSchema,
  type FeedbackFormData,
} from "@/schemas/user.schema";

const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MiB
const ALLOWED_TYPES = [
  "application/pdf",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg",
  "image/png",
  "image/webp",
];

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCategory?: string;
  initialMessage?: string;
}

export function FeedbackModal({
  open,
  onOpenChange,
  initialCategory,
  initialMessage,
}: FeedbackModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      category: "",
      message: "",
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    form.reset({
      category: initialCategory ?? "",
      message: initialMessage ?? "",
    });
    setSelectedFiles([]);
  }, [open, initialCategory, initialMessage, form]);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_BYTES) {
      return `${file.name}: max size is 5 MB`;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `${file.name}: type not allowed (PDF, CSV, Excel, images only)`;
    }
    return null;
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const next: File[] = [];
    const errors: string[] = [];
    for (const file of files) {
      if (selectedFiles.length + next.length >= MAX_ATTACHMENTS) {
        errors.push("Maximum 5 attachments");
        break;
      }
      const err = validateFile(file);
      if (err) errors.push(err);
      else next.push(file);
    }
    if (errors.length) toast.error(errors[errors.length - 1]);
    setSelectedFiles((prev) => [...prev, ...next].slice(0, MAX_ATTACHMENTS));
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (values: FeedbackFormData) => {
    try {
      setSubmitting(true);
      let attachments: CreateFeedbackAttachmentPayload[] | undefined;

      if (selectedFiles.length > 0) {
        const intents = await requestFeedbackUploadIntents(
          selectedFiles.map((f) => ({
            filename: f.name,
            content_type: f.type,
            size_bytes: f.size,
          }))
        );
        for (let i = 0; i < selectedFiles.length; i++) {
          const intent = intents[i];
          if (!intent) throw new Error("Missing upload intent");
          await uploadFileToSignedUrl(
            intent.upload_url,
            intent.form_fields,
            selectedFiles[i]
          );
          attachments = attachments ?? [];
          attachments.push({
            attachment_id: intent.attachment_id,
            object_key: intent.object_key,
            original_filename: selectedFiles[i].name,
            content_type: selectedFiles[i].type,
            size_bytes: selectedFiles[i].size,
          });
        }
      }

      await createFeedback({
        category: values.category,
        message: values.message,
        attachments,
      });
      toast.success("Thank you for your feedback!");
      form.reset();
      setSelectedFiles([]);
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>
            Help us improve by sharing your thoughts or reporting bugs.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="improvement">Improvement</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe your feedback in detail..."
                      className="min-h-[120px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Attachments (optional)</FormLabel>
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_TYPES.join(",")}
                  className="hidden"
                  onChange={onFileChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting || selectedFiles.length >= MAX_ATTACHMENTS}
                >
                  <Paperclip className="h-4 w-4 mr-2" />
                  Add files (max {MAX_ATTACHMENTS}, 5 MB each)
                </Button>
                {selectedFiles.length > 0 && (
                  <ul className="text-sm space-y-1">
                    {selectedFiles.map((file, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between gap-2 rounded border px-2 py-1 bg-muted/50"
                      >
                        <span className="truncate flex-1" title={file.name}>
                          {file.name}
                        </span>
                        <span className="text-muted-foreground text-xs shrink-0">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={() => removeFile(i)}
                          disabled={submitting}
                          aria-label="Remove file"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </FormItem>

            <DialogFooter className="pt-4">
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Submit
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
